import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { AuthContext, AuthUser } from '@/lib/auth'
import { supabase } from '@/lib/supabase'
import { ensureProfile } from '@/lib/profile'
import { OAUTH_REDIRECT_URL } from "@/lib/auth"

// VOY-478: `/internal/hero-mode-preview` is gone (preview route + page
// deleted in this ticket per the VOY-476 TODO). `/search-plus` STAYS
// in the public list — it's now a redirect component, but the auth
// gate must let unauthenticated traffic through so bookmarks /
// marketing emails / RecentSearchesRow handoffs land cleanly on `/`.
const PUBLIC_ROUTES = ['/', '/auth/sign-in', '/auth/callback', '/search', '/subscription', '/games', '/quiz', '/search-plus']
const VITE_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser>(null)
  const [session, setSession] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: string })?.from || '/profile'

  const handleSession = useCallback(async (session: any) => {
    try {
      if (session?.user) {
        try {
          const userId = session?.user?.id;

          if (!userId) {
            console.warn('[AuthProvider] Could not extract user ID from session');
            return;
          }

          await ensureProfile(userId);
          setUser(session.user);

          const accessToken = session.access_token
          const refreshToken = session.refresh_token
          if (accessToken) {
            await fetch(`${VITE_API_URL}/api/auth/set-cookie`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ access_token: accessToken, refresh_token: refreshToken }),
            })
          }

          if (location.pathname === '/auth/sign-in') {
            navigate(from)
          }
        } catch (error) {
          console.error('Error ensuring profile:', error)
          setUser(session.user)
        }
      } else {
        setUser(null)
        const isProtectedRoute = !PUBLIC_ROUTES.includes(location.pathname)
        if (isProtectedRoute) {
          navigate('/auth/sign-in', { state: { from: location.pathname } })
        }
      }
    } catch (error) {
      console.error('Error handling session:', error)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [navigate, location.pathname, from])

  useEffect(() => {
    async function initializeAuth() {
      const { data: { session } } = await supabase.auth.getSession()
  
      if (session) {
        await handleSession(session)
      } else {
        setLoading(false)
      }
  
      supabase.auth.onAuthStateChange((_event, session) => {
        handleSession(session)
      })
    }
  
    initializeAuth()
  }, [handleSession])
  
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error

    const accessToken = data.session?.access_token
    const refreshToken = data.session?.refresh_token
    if (accessToken) {
      await fetch(`${VITE_API_URL}/api/auth/set-cookie`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ access_token: accessToken, refresh_token: refreshToken }),
      })
    }
  }

  const signOut = async () => {
    try {
      await supabase.auth.signOut()
  
      await fetch(`${VITE_API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      })
  
      // 🔄 Double-check cookies are cleared
      await fetch(`${VITE_API_URL}/api/auth/me`, {
        credentials: 'include',
      })
  
      setUser(null)
      setSession(null)
      navigate('/')
    } catch (error) {
      console.error('Error signing out:', error)
      throw error
    }
  }
  
  

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: OAUTH_REDIRECT_URL },
    })
    if (error) throw error
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-3.5rem)]">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <AuthContext.Provider value={{ user, session, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}
